import React from 'react'
import {
    fetchAllDynamicallyInstrumentedFunctions,
    dynamicallyInstrumentFunction,
} from '../redux/V2/DynamicallyInstrumentedFunction/actions'
import { connect } from 'react-redux'
import { ApplicationState } from '../redux/ApplicationState'
import {
    extractClassnameAndFunction,
    isFunctionAlreadyInstrumented,
} from '../redux/V2/DynamicallyInstrumentedFunction/DynamicallyInstrumentedFunctionState'
import { isInstrumentedExtensionFunction } from './instrumentedExtensionFunctions'
import { trackUserEvent } from '../redux/V2/TrackUserEvent/actions'

interface OwnProps {
    details: { name?: string; namespace: string; ct: number; wt: number }
}

interface DispatchProps {
    dynamicallyInstrumentedFunction: typeof dynamicallyInstrumentFunction
    fetchAllDynamicallyInstrumentedFunctions: typeof fetchAllDynamicallyInstrumentedFunctions
    trackUserEvent: typeof trackUserEvent
}

interface StateProps {
    isFunctionAlreadyInstrumented: boolean
    loaded: boolean
    loading: boolean
    error: boolean
    errorMessage: string
}

type Props = OwnProps & DispatchProps & StateProps

export class DynamicallyInstrumentedFunctionComponent extends React.Component<Props> {
    public componentDidMount = (): void => {
        // prevent overfetching
        // 1. if already loaded previously
        // 2. if async call is in progress from another component
        if (!this.props.loaded && !this.props.loading) {
            this.props.fetchAllDynamicallyInstrumentedFunctions()
        }
    }

    public render() {
        if (this.props.details.ct > 20 || this.props.details.wt < 5000) {
            return null
        }

        const { details } = this.props

        const [className, functionName] = extractClassnameAndFunction(details)

        let classNameString = 'tw-mr-8 tw-tooltip tw-tooltip-dataText tw-tooltip--bottom'
        if (this.props.loading) {
            classNameString += ' cursor-progress'
        }

        const functionIncludesClosureOrAnonymousClass = details.name?.includes('{closure') || details.name?.includes('class@anonymous')

        if (functionIncludesClosureOrAnonymousClass) {
            return null
        }

        if (isInstrumentedExtensionFunction(className, functionName)) {
            return null
        }

        const regex = /\[.*\]/
        if (regex.test(functionName)) {
            return null
        }

        if (this.props.error) {
            return (
                <a className={classNameString} data-tooltip-text={this.props.errorMessage}>
                    <i className="fa fa-crosshairs" style={{ verticalAlign: 'middle', fontSize: '18px', color: 'grey' }}></i>
                </a>
            )
        }

        if (!this.props.isFunctionAlreadyInstrumented) {
            return (
                <a
                    className={classNameString}
                    data-tooltip-text={'Dynamically instrument this function in Timeline/Flamegraph Profiler'}
                    onClick={() => {
                        this.props.dynamicallyInstrumentedFunction(
                            JSON.stringify({
                                className: className,
                                functionName: functionName,
                            }),
                        )
                        this.props.trackUserEvent('dynamicallyInstrumentFunction')
                    }}
                >
                    <i className="fa fa-crosshairs" style={{ verticalAlign: 'middle', fontSize: '18px' }}></i>
                </a>
            )
        }

        if (this.props.isFunctionAlreadyInstrumented) {
            return (
                <a
                    className="tw-mr-8 tw-tooltip tw-tooltip-dataText tw-tooltip--bottom"
                    data-tooltip-text={'This function is already instrumented in Timeline/Flamegraph Profiler'}
                    onClick={() => {
                        this.props.dynamicallyInstrumentedFunction(
                            JSON.stringify({
                                className,
                                functionName,
                            }),
                        )
                    }}
                >
                    <i className="fa fa-crosshairs" style={{ verticalAlign: 'middle', fontSize: '18px', color: 'green' }}></i>
                </a>
            )
        }

        return null
    }
}

export const DynamicallyInstrumentedFunction = connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (globalState, ownProps): StateProps => {
        const [className, functionName] = extractClassnameAndFunction(ownProps.details)

        return {
            isFunctionAlreadyInstrumented: isFunctionAlreadyInstrumented(globalState, className, functionName || ''),
            loaded: globalState.v2.dynamicallyInstrumentedFunctions.loaded,
            loading: globalState.v2.dynamicallyInstrumentedFunctions.loading,
            error: globalState.v2.dynamicallyInstrumentedFunctions.error,
            errorMessage: globalState.v2.dynamicallyInstrumentedFunctions.errorMessage,
        }
    },
    {
        dynamicallyInstrumentedFunction: dynamicallyInstrumentFunction,
        fetchAllDynamicallyInstrumentedFunctions: fetchAllDynamicallyInstrumentedFunctions,
        trackUserEvent: trackUserEvent,
    },
)(DynamicallyInstrumentedFunctionComponent)
